"use client";

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { Button, ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

// Define interface for button data including optional bordered prop
interface CTAButton extends ButtonProps {
  text: string;
  href: string;
}

const candidateButtons: CTAButton[] = [
  {
    text: "Profil erstellen",
    href: "/contact?preselect=candidate",
    variant: "light-secondary",
    className: undefined
  },
  {
    text: "Mehr erfahren",
    href: "/candidates",
    variant: "light-secondary",
    bordered: true,
    className: undefined
  }
];

const companyButtons: CTAButton[] = [
  {
    text: "Beratungsgespräch",
    href: "/contact?preselect=company",
    variant: "light-primary",
    className: undefined
  },
  {
    text: "Mehr erfahren",
    href: "/companies",
    variant: "light-primary",
    bordered: true,
    className: undefined
  }
];

export function DualCTA() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Kandidaten CTA */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center rounded-2xl bg-gradient-to-r from-thistle-50 to-thistle-100 p-8 md:p-10"
          >
            <h2 className="text-h3 mb-6">Für Kandidaten</h2>
            <p className="text-body mb-8 min-h-[80px]">
              Starten Sie jetzt mit umynd und finden Sie den idealen Job, der Ihren Fähigkeiten und Wünschen entspricht.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {candidateButtons.map((button, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                  className="w-full sm:w-auto"
                >
                  <Link href={button.href} passHref>
                    <Button
                      variant={button.variant}
                      bordered={button.bordered}
                      size="default"
                      className={cn(
                        "w-full sm:w-auto",
                        "whitespace-nowrap"
                      )}
                    >
                      {button.text}
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Unternehmen CTA */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center rounded-2xl bg-gradient-to-r from-true-blue-50 to-true-blue-100 p-8 md:p-10"
          >
            <h2 className="text-h3 mb-6">Für Unternehmen</h2>
            <p className="text-body mb-8 min-h-[80px]">
              Optimieren Sie Ihr Recruiting mit modernster KI-Technologie und finden Sie die perfekten Mitarbeiter für Ihr Team.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {companyButtons.map((button, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                  className="w-full sm:w-auto"
                >
                  <Link href={button.href} passHref>
                    <Button
                      variant={button.variant}
                      bordered={button.bordered}
                      size="default"
                      className={cn(
                        "w-full sm:w-auto",
                        "whitespace-nowrap"
                      )}
                    >
                      {button.text}
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}